import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { currencyRowsFromResponseData, normalizeCurrencyRow } from "../utils/currencyList";
import { mapCustomerListItemFromApi, getCustomerSelectLabel } from "../utils/formatters";
import { createCustomerAccount } from "./customerAccountService";
import "../assets/css/Issues.css";
import "../assets/css/Projects.css";
import "../assets/css/ViewCustomer.css";
import { getAuthItem } from "../utils/authStorage";
import { SETTINGS_TABS, settingsPath } from "../utils/financeTerms";

const ACCOUNT_TYPES = ["Savings", "Current"];
const STATUS_OPTIONS = ["Active", "Inactive"];

const initialForm = {
  client_id: "",
  Account_Holder_Name: "",
  Account_Number: "",
  Account_Type: "Savings",
  IFSC_Code: "",
  Bank_Name: "",
  Branch: "",
  UPI_Id: "",
  Currency_Id: "",
  Is_Primary: true,
  Status: "Active",
  Notes: ""
};

function getCreatedByFromStorage() {
  try {
    const userStr = getAuthItem("user");
    if (!userStr) return 1;
    const user = JSON.parse(userStr);
    return user.user_id ?? user.id ?? 1;
  } catch {
    return 1;
  }
}

function AddCustomerAccount() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCustomers(true);
      try {
        const response = await axiosInstance.get("/clients");
        const raw = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        const mapped = raw.map(mapCustomerListItemFromApi).filter(Boolean);
        if (!cancelled) setCustomers(mapped);
      } catch {
        if (!cancelled) setCustomers([]);
      } finally {
        if (!cancelled) setLoadingCustomers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCurrencies(true);
      try {
        const response = await axiosInstance.get("/currencies");
        const rows = currencyRowsFromResponseData(response.data);
        const mapped = rows.map(normalizeCurrencyRow).filter(Boolean);
        if (!cancelled) setCurrencies(mapped);
      } catch {
        if (!cancelled) setCurrencies([]);
      } finally {
        if (!cancelled) setLoadingCurrencies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      client_id: Number(form.client_id),
      Account_Holder_Name: String(form.Account_Holder_Name || "").trim(),
      Account_Number: String(form.Account_Number || "").trim(),
      Account_Type: form.Account_Type || "Savings",
      IFSC_Code: String(form.IFSC_Code || "").trim(),
      Bank_Name: String(form.Bank_Name || "").trim(),
      Branch: String(form.Branch || "").trim(),
      UPI_Id: String(form.UPI_Id || "").trim(),
      Currency_Id: Number(form.Currency_Id),
      Is_Primary: form.Is_Primary ? 1 : 0,
      Status: form.Status || "Active",
      Notes: String(form.Notes || "").trim(),
      Created_By: getCreatedByFromStorage()
    };

    if (!payload.client_id || !payload.Account_Holder_Name || !payload.Account_Number || !payload.Bank_Name || !payload.IFSC_Code || !payload.Currency_Id) {
      setError("Client, account holder name, account number, bank name, IFSC, and currency are required.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await createCustomerAccount(payload);
      navigate(settingsPath(SETTINGS_TABS.clientAccounts), { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to save client account.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="issues-container">
      <div className="viewlead-header" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="viewlead-back-link"
          onClick={() => navigate(settingsPath(SETTINGS_TABS.clientAccounts))}
        >
          <FaArrowLeft /> Back
        </button>
      </div>
      <div className="issues-main-card">
        <form className="issues-form" onSubmit={handleSubmit}>
          {error ? <div className="issues-alert issues-alert-error">{error}</div> : null}
          <div className="issues-form-grid">
            <div className="form-group">
              <label>Client <span className="required">*</span></label>
              <select name="client_id" value={form.client_id} onChange={handleFormChange} disabled={loadingCustomers}>
                <option value="">{loadingCustomers ? "Loading..." : "Select customer"}</option>
                {customers.map((c) => (
                  <option key={c.id} value={String(c.id)}>{getCustomerSelectLabel(c)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Account Holder Name <span className="required">*</span></label>
              <input name="Account_Holder_Name" value={form.Account_Holder_Name} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Account Number <span className="required">*</span></label>
              <input name="Account_Number" value={form.Account_Number} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Account Type</label>
              <select name="Account_Type" value={form.Account_Type} onChange={handleFormChange}>
                {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>IFSC Code <span className="required">*</span></label>
              <input name="IFSC_Code" value={form.IFSC_Code} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Bank Name <span className="required">*</span></label>
              <input name="Bank_Name" value={form.Bank_Name} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <input name="Branch" value={form.Branch} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>UPI Id</label>
              <input name="UPI_Id" value={form.UPI_Id} onChange={handleFormChange} />
            </div>
            <div className="form-group">
              <label>Currency <span className="required">*</span></label>
              <select name="Currency_Id" value={form.Currency_Id} onChange={handleFormChange} disabled={loadingCurrencies}>
                <option value="">{loadingCurrencies ? "Loading..." : "Select currency"}</option>
                {currencies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.code ? `${c.code} — ${c.name || c.code}` : c.name || `ID ${c.id}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="Status" value={form.Status} onChange={handleFormChange}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" name="Is_Primary" checked={form.Is_Primary} onChange={handleFormChange} />
                Primary account
              </label>
            </div>
            <div className="form-group issues-description-full">
              <label>Notes</label>
              <textarea name="Notes" value={form.Notes} onChange={handleFormChange} rows={3} />
            </div>
          </div>
          <div className="issues-form-actions" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="submit"
              className="btn-primary btn-add-lead btn-save-customer-account"
              disabled={submitting}
            >
              {submitting ? "Saving..." : "Save Client Account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCustomerAccount;
